import { AvatarSize, RawAvatar } from "@components/Avatar";
import { Button, ButtonRole, ButtonTargetKind } from "@components/Button";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React from "react";

import {
  getRoutePath,
  ClientRouteName,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";

import { ReactComponent as UserImage } from "src/assets/illustrations/UserAvatarPlaceholder.svg";
import { ReactComponent as NonprofitImage } from "src/assets/illustrations/nonprofit_color.svg";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import { SignUpSideImage } from "src/pages/Signup/SingUpSideImage";
import { colorCssVars } from "src/theme/color";
import { MediaSize, cssForMediaSize } from "src/theme/mediaQueries";
import { spacing, verticalStackCss } from "src/theme/spacing";
import { textSizeCss } from "src/theme/text";

const avatarPlaceholderCss = css`
  width: 100%;
  height: auto;
`;

const StyledButton = styled(Button)`
  border: 2px solid var(${colorCssVars.accent.large});
  color: var(${colorCssVars.accent.large});
  padding: ${spacing.s} ${spacing.xl} ${spacing.s} ${spacing.s};
  width: 100%;
  border-radius: 500px;

  &:hover {
    border: 2px solid var(${colorCssVars.accent.largeHighlight});
    color: var(${colorCssVars.accent.largeHighlight});
  }

  .edo-account-type-selector-content {
    display: flex;
    align-items: center;
    gap: ${spacing.m};
    text-align: left;

    strong {
      font-weight: bold;
      ${textSizeCss.s};
    }
  }

  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: css`
      .edo-account-type-selector-content {
        strong {
          font-weight: bold;
          ${textSizeCss.m};
        }
      }
    `,
  })}
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.l};
  margin-top: ${spacing.xl};

  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: css`
      margin-top: ${spacing.l};
      grid-template-columns: 2fr 1fr;
    `,
  })}

  ${cssForMediaSize({
    min: MediaSize.X_LARGE,
    css: css`
      grid-template-columns: 1fr 1fr;
    `,
  })}
`;

export const SignUpAccountTypeSelector = () => {
  const { search } = useEdoRouter();
  const searchParamsObj = Object.fromEntries(new URLSearchParams(search));

  return (
    <React.Fragment>
      <div css={verticalStackCss.xs}>
        <h1>Sign up</h1>
        <p css={{ color: `var(${colorCssVars.text.secondary})` }}>
          Please choose the type of account you would like to create:
        </p>
      </div>
      <Grid>
        <div css={[verticalStackCss.xs, { justifyContent: "center" }]}>
          <StyledButton
            role={ButtonRole.UNSTYLED}
            data-tname="sign-up-account-type-selector_donor"
            contentClassName="edo-account-type-selector-content"
            onClick={{
              kind: ButtonTargetKind.LINK,
              to: getRoutePath({
                name: ClientRouteName.SIGNUP,
                format: URLFormat.RELATIVE,
                query: {
                  ...searchParamsObj,
                },
              }),
            }}
          >
            <RawAvatar
              size={AvatarSize.MEDIUM}
              placeholder={<UserImage css={avatarPlaceholderCss} />}
            />
            <strong>Sign up as donor</strong>
          </StyledButton>
          <StyledButton
            role={ButtonRole.UNSTYLED}
            data-tname="sign-up-account-type-selector_nonprofit-admin"
            contentClassName="edo-account-type-selector-content"
            onClick={{
              kind: ButtonTargetKind.LINK,
              to: getRoutePath({
                name: ClientRouteName.NONPROFITS_ONBOARDING_SEARCH,
                format: URLFormat.RELATIVE,
                query: {
                  ...searchParamsObj,
                },
              }),
            }}
          >
            <RawAvatar
              size={AvatarSize.MEDIUM}
              placeholder={<NonprofitImage css={avatarPlaceholderCss} />}
            />
            <strong>Sign up as admin for a nonprofit</strong>
          </StyledButton>
        </div>
        <SignUpSideImage css={{ margin: "0 auto" }} />
      </Grid>
    </React.Fragment>
  );
};
